import React, { useEffect, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  CheckCircle,
  Circle,
} from "lucide-react";
import { toast } from "react-toastify";
import { SessionQuery } from "./types";
import { useSearchParams } from "react-router-dom";
import { userDataStore } from "../../../../../../store/userData";
import { useSnapshot } from "valtio";
import { AiOutlineComment } from "react-icons/ai";

interface SidebarItem {
  label: string;
  count: number;
}

interface SidebarProps {
  items: SidebarItem[];
  maxWidth?: number;
  collapsedWidth?: number;
  sessionCompleted: boolean;
  onInterviewOff: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  items,
  collapsedWidth = 80,
  maxWidth = 280,
  sessionCompleted,
  onInterviewOff,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  //
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionTemp = searchParams.get(SessionQuery.sessionCount);
  const { userData } = useSnapshot(userDataStore);
  const [maxSessionCount, setMaxSessionCount] = useState<number>(0);

  useEffect(() => {
    if (userData) setMaxSessionCount(userData.maxSessionCount as number);
  }, [userData]);

  // Check if the device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Ensure sidebar is collapsed by default on mobile
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
    }
  }, [isMobile]);

  const toggleSidebar = () => setCollapsed((prev) => !prev);

  const onStartNewSession = () => {
    if (Number(items.length) >= maxSessionCount || !sessionCompleted) {
      toast.error(
        "You either have an active session or have reached the maximum number of allowed sessions"
      );
      return;
    }

    searchParams.set(SessionQuery.sessionCount, `${items.length + 1}`);
    setSearchParams(searchParams);
    onInterviewOff();

    // Collapse sidebar after starting a new session on mobile
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const getCompletionIcon = (completed: boolean = false) => {
    return completed ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <Circle className="w-4 h-4 text-gray-400" />
    );
  };

  const onBtnClick = (item: SidebarItem) => {
    searchParams.set(SessionQuery.sessionCount, `${item.count}`);
    setSearchParams(searchParams);

    // Collapse sidebar after selecting a session on mobile
    if (isMobile) {
      setCollapsed(true);
    }
  };

  return (
    <>
      <div
        className={`flex flex-col transition-all duration-300 ease-in-out bg-white border-r border-gray-200 shadow-lg ${
          isMobile ? "fixed top-24 left-0 z-40" : ""
        } ${isMobile && !collapsed ? "h-full" : ""}`}
        style={{
          width: collapsed ? collapsedWidth : maxWidth,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b border-gray-200 cursor-pointer bg-gradient-to-r from-blue-50 to-indigo-50"
          onClick={toggleSidebar}
        >
          <div
            className={`flex items-center gap-3 ${
              collapsed ? "justify-center" : ""
            }`}
          >
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
              <AiOutlineComment className="w-4 h-4 text-white" />
              {/* <Sparkles className="w-4 h-4 text-white" /> */}
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-semibold text-gray-900">Sessions</h1>
                <p className="text-xs text-gray-500">
                  • {userData?.interviewCount ?? "--"}/{maxSessionCount} used
                </p>
              </div>
            )}
          </div>

          <button
            className="p-2 transition-colors rounded-lg hover:bg-white/60"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            )}
          </button>
        </div>

        {(isMobile ? !collapsed : true) && (
          <>
            {/* New Session Button */}
            <div className="p-4 border-b border-gray-200 ">
              <button
                onClick={onStartNewSession}
                disabled={items.length >= maxSessionCount || !sessionCompleted}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200 border text-sm ${
                  items.length >= maxSessionCount || !sessionCompleted
                    ? "bg-gray-100 border-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-primaryColor text-white shadow-md hover:shadow-lg transform hover:bg-white hover:border-primaryColor hover:text-primaryColor "
                } ${collapsed ? "justify-center px-2" : ""}`}
              >
                <Plus
                  className={`w-5 h-5 ${
                    collapsed && !sessionCompleted ? "animate-pulse" : ""
                  }`}
                />
                {!collapsed && (
                  <span>
                    {sessionCompleted ? "New Session" : "Session Active"}
                  </span>
                )}
              </button>
            </div>

            {/* Sessions List */}
            <div className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
              <div className="p-4 space-y-2">
                {items.length === 0 ? (
                  <div className="py-8 text-center">
                    <AiOutlineComment className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    {/* <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" /> */}
                    <p className="mb-1 text-sm font-medium text-gray-500">
                      No sessions yet
                    </p>
                    <p className="text-xs text-gray-400">
                      Start your first session above
                    </p>
                  </div>
                ) : (
                  items.map((item, i) => {
                    const isActive = sessionTemp
                      ? item.count === +sessionTemp
                      : false;
                    const itemCompleted = !!(
                      userData?.interviewCount &&
                      i + 1 < userData.interviewCount
                    );

                    return collapsed ? (
                      <button
                        key={i}
                        onClick={() => onBtnClick(item)}
                        className={`w-full p-3 rounded-lg transition-all duration-200 flex items-center justify-center relative ${
                          isActive
                            ? "bg-blue-100 border-2 border-primaryColor/55"
                            : "hover:bg-gray-100 border-2 border-transparent"
                        }`}
                        title={`${item.label} - ${
                          itemCompleted ? "Completed" : "In Progress"
                        }`}
                      >
                        <span className="text-sm font-semibold text-gray-700">
                          {item.count}
                        </span>
                        {itemCompleted && (
                          <div className="absolute w-3 h-3 bg-green-500 border-2 border-white rounded-full -top-1 -right-1"></div>
                        )}
                      </button>
                    ) : (
                      <button
                        key={i}
                        onClick={() => onBtnClick(item)}
                        className={`w-full text-left p-4 rounded-xl transition-all duration-200 group ${
                          isActive
                            ? "bg-blue-100 border-2 border-primaryColor/55 shadow-md"
                            : "hover:bg-gray-50 border-2 border-transparent"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                                isActive
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-gray-100 text-gray-600 group-hover:bg-gray-200"
                              }`}
                            >
                              {item.count}
                            </div>
                            <div className="flex flex-col items-start">
                              <span className="text-sm font-medium text-gray-900">
                                {item.label}
                              </span>
                              <div className="flex items-center gap-1 mt-1">
                                {getCompletionIcon(itemCompleted)}
                                <span
                                  className={`text-xs ${
                                    itemCompleted
                                      ? "text-green-600"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {itemCompleted ? "Completed" : "In Progress"}
                                </span>
                              </div>
                            </div>
                          </div>
                          {isActive && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile Overlay */}
      {isMobile && !collapsed && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Sidebar;
